import type { DateTime } from 'luxon'
import { DateTime as LuxonDateTime } from 'luxon'
import { RoomStateStatus } from '../../firestore/RoomState/types'

export type MeetingResultsState =
  | 'not-yet-completed'
  | 'never-started'
  | 'processing'
  | 'processing-delayed'
  | 'failed'
  | 'ready'

/** Minutes of processing before the copy escalates to "taking longer than expected". */
export const NORMAL_RESULTS_PROCESSING_MINUTES = 10

/** Server retry ceiling — mirrors room_state_ai_trigger.go and watchdog.ts
 *  (both stop at 4); must change with them. */
export const MAX_AI_ATTEMPTS = 4

/** The subset of a `room_state/{roomId}/ai` doc the state machine needs. */
export type MeetingResultsAIRun = {
  success: boolean | null | undefined
  endedAt: Date | null | undefined
  startedAt?: Date | null
}

/** Pure state machine for the meeting-results surface. */
export function computeMeetingResultsState({
  roomStateStatus,
  roomStartedAt,
  activeSlideChangedAt,
  summaryHasContent,
  roomGradedValue,
  aiRuns,
  now,
}: {
  roomStateStatus: RoomStateStatus
  roomStartedAt: Date | null | undefined
  activeSlideChangedAt: Date | null | undefined
  /** Doc existence is not enough — a failed summarizer persists `summary: ''`. */
  summaryHasContent: boolean
  roomGradedValue: boolean | null
  /** All grading attempts, or `null` while the stream hasn't reported. */
  aiRuns: MeetingResultsAIRun[] | null
  now: DateTime
}): MeetingResultsState {
  // Status under-reports ended-ness (the meeting cubit's library object has
  // no grading input), so grading artifacts or any ai run also count.
  const sessionEnded =
    roomStateStatus === RoomStateStatus.completed ||
    summaryHasContent ||
    roomGradedValue === true ||
    (aiRuns !== null && aiRuns.length > 0)
  if (!sessionEnded) {
    return 'not-yet-completed'
  }
  // Run success alone is ready: demos surface results mid-session from a
  // partial run, which writes neither engagement docs nor a summary. Ready
  // also outranks never-started — results beat a missing roomStartedAt.
  if (
    summaryHasContent ||
    roomGradedValue === true ||
    aiRuns?.some((run) => run.success === true)
  ) {
    return 'ready'
  }
  if (!roomStartedAt) {
    return 'never-started'
  }
  // Failed means the server gave up; an in-flight doc (no endedAt) suppresses it.
  if (
    aiRuns !== null &&
    aiRuns.length >= MAX_AI_ATTEMPTS &&
    !aiRuns.some((run) => run.success === true) &&
    !aiRuns.some((run) => !run.endedAt)
  ) {
    return 'failed'
  }
  // Newest run start beats slide activity, so a fresh Force Complete shows
  // `processing`, not immediate `processing-delayed`.
  const activityAt = activeSlideChangedAt ?? roomStartedAt
  const latestRunStart =
    aiRuns?.reduce<Date | null>(
      (latest, run) =>
        run.startedAt && (!latest || run.startedAt > latest)
          ? run.startedAt
          : latest,
      null
    ) ?? null
  const reference = LuxonDateTime.fromJSDate(
    latestRunStart && latestRunStart > activityAt ? latestRunStart : activityAt
  )
  const elapsedMinutes = now.diff(reference).as('minutes')
  return elapsedMinutes >= NORMAL_RESULTS_PROCESSING_MINUTES
    ? 'processing-delayed'
    : 'processing'
}
